import { useMainContext } from '@/lib/context';
import { FILTERS } from '@/lib/fixedTypes';
import {
  getAutocompleteOptions,
  getDynamicTurnout,
  getResults,
} from '@/lib/getResults';
import { getMapSettings } from '@/lib/levelsHelpers';
import { AutocompleteOption, DataLevel, ResultCodes } from '@/lib/model';
import * as React from 'react';
import { MainComponent } from './MainComponent';

export const Main = () => {
  const [searchCodes, setSearchCodes] = React.useState<ResultCodes | null>(
    null
  );
  const {
    selectedYear,
    selectedRegion,
    toggleRegion,
    dataLevel,
    setDataLevel,
    setResults,
    setMainResult,
    handleResult,
    selectedResults,
    lastResult,
    lastProvince,
    isComuneLoading,
    setComuneIsLoading,
    dynamicTurnout,
    setDynamicTurnout,
    lastVersion,
  } = useMainContext();

  // Test print dinamically the URL of the page in which is embedded
  React.useEffect(() => {
    console.log('URL of the page in which is embedded', window.location.href);
  }, []);

  // Dynamic turnout
  React.useEffect(() => {
    const fn = async () => {
      const lastTurnout = await getDynamicTurnout();

      setDynamicTurnout(lastTurnout);
    };

    fn();
  }, [lastVersion, setDynamicTurnout]);

  // All results
  React.useEffect(() => {
    // don't fetch results if there is no dynamic turnout or we're currently finding by comune
    if (
      !dynamicTurnout ||
      searchCodes ||
      (lastProvince && lastProvince.region !== selectedRegion)
    ) {
      return;
    }

    const getFetchResults = () => {
      // console.log( 'getFetchResult', dataLevel)
      // get comuni by province id
      if (dataLevel === DataLevel.COMUNE && lastProvince) {
        // console.log('GETRESULTS livello comune',selectedYear, selectedRegion, dataLevel, dynamicTurnout, lastProvince.idCodes )
        return getResults(
          true,
          selectedYear,
          selectedRegion,
          dataLevel,
          dynamicTurnout,
          lastProvince.idCodes
        );
      }

      // console.log('GETRESULTS livello all data',selectedYear, selectedRegion, dataLevel, dynamicTurnout )
      // get all data
      return getResults(
        true,
        selectedYear,
        selectedRegion,
        dataLevel,
        dynamicTurnout
      );
    };

    const fn = async () => {
      const results = await getFetchResults();
      setResults(results);
    };

    fn();

    // we add last version so getResult refetches when it changes
  }, [
    selectedYear,
    selectedRegion,
    dataLevel,
    lastProvince,
    setResults,
    dynamicTurnout,
    searchCodes,
    lastVersion,
  ]);

  // Main result
  React.useEffect(() => {
    const fn = async () => {
      // console.log('GETRESULTS livello regione',selectedYear, selectedRegion, DataLevel.REGIONE, dynamicTurnout );

      let mainResult = (
        await getResults(
          false,
          selectedYear,
          selectedRegion,
          DataLevel.REGIONE, // main result is always region
          dynamicTurnout
        )
      )[0];

      if (!mainResult) {
        return;
      }

      // let coordinates: number[] = [];
      // switch (selectedRegion) {
      //   case Region.LAZIO:
      //     coordinates = [12.9896, 41.6552];
      //     break;
      //   case Region.LOMBARDIA:
      //     coordinates = [9.8452, 45.4791];
      //     break;
      //   case Region.ABRUZZO:
      //     coordinates = [12.6489739, 42.017156];
      //     break;
      //   default: // Italy
      //     coordinates = [14.2430069, 40.8536072];
      // }

      mainResult = {
        ...mainResult,
        mapLayer: {
          ...mainResult.mapLayer,
          // coordinates
        },
      };

      setMainResult(mainResult);
    };

    fn();
    // we add last version so getResult refetches when it changes
  }, [
    setMainResult,
    lastVersion,
    selectedYear,
    selectedRegion,
    dynamicTurnout,
  ]);

  // this effect find by comune after its own region has been fully loaded/toggled
  React.useEffect(() => {
    if (
      !selectedResults.length ||
      !searchCodes ||
      selectedResults[0].idCodes.codReg !== searchCodes.codReg
    ) {
      return;
    }

    const fn = async () => {
      try {
        // console.log('GETRESULTS livello provinces',selectedYear, selectedRegion, DataLevel.PROVINCIA, dynamicTurnout );
        const provinces = await getResults(
          false,
          selectedYear,
          selectedRegion,
          DataLevel.PROVINCIA,
          dynamicTurnout
        );

        const province = provinces.find(
          province => province.idCodes.codProv === searchCodes.codProv
        );

        // console.log('GETRESULTS livello provinces',selectedYear, selectedRegion, DataLevel.COMUNE, dynamicTurnout, searchCodes );
        const comuni = await getResults(
          false,
          selectedYear,
          selectedRegion,
          DataLevel.COMUNE,
          dynamicTurnout,
          searchCodes
        );

        const comune = comuni.find(
          c =>
            c.idCodes.codProv === searchCodes.codProv &&
            c.idCodes.codCom === searchCodes.codCom
        );

        if (!province || !comune) {
          return;
        }

        handleResult([province, comune], false);
        setDataLevel(DataLevel.COMUNE);
      } catch (e) {
        console.error('e', e);
      } finally {
        setComuneIsLoading(false);
        setSearchCodes(null);
      }
    };

    fn();
  }, [
    selectedResults,
    selectedYear,
    selectedRegion,
    dynamicTurnout,
    isComuneLoading,
    searchCodes,
    handleResult,
    setDataLevel,
    setComuneIsLoading,
  ]);

  const mainResultMapSettings = React.useMemo(
    () => getMapSettings(DataLevel.MAIN),
    []
  );

  const mapSettings = React.useMemo(() => {
    let settings = getMapSettings(dataLevel);

    if (lastResult?.level === DataLevel.MAIN) {
      settings = {
        ...settings,
        zoom: mainResultMapSettings.zoom,
      };
    }

    return settings;
  }, [dataLevel, lastResult, mainResultMapSettings]);

  const filters = FILTERS.filter(f => {
    // hide regione and national level
    return f.value > DataLevel.REGIONE;
  });

  // Function used when pressing an option within searched results
  const onOptionPress = async (value: AutocompleteOption) => {
    setComuneIsLoading(true);

    const codes: ResultCodes = {
      codCirc: value.cod_circ,
      codClPlu: value.cod_plu,
      codClUni: value.cod_cl_uni,
      codCom: value.cod_comune,
      codProv: value.cod_provincia,
      codReg: value.cod_regione,
    };

    // toggle if we're in a different region
    if (
      selectedResults[0].idCodes.codReg !== undefined &&
      codes.codReg !== undefined
    ) {
      let selReg = parseInt(selectedResults[0].idCodes.codReg);
      if (selReg !== parseInt(codes.codReg)) {
        toggleRegion();
      }
    }

    // set search codes, wait for region to be toggled (if needed), and activate useEffect defined above
    setSearchCodes(codes);
  };

  const inputOptions = getAutocompleteOptions();

  if (!selectedResults.length) {
    return null;
  }

  return (
    <MainComponent
      inputOptions={inputOptions}
      onOptionPress={onOptionPress}
      mapSettings={mapSettings}
      filters={filters}
    />
  );
};
